import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  MenuIcon, 
  XIcon, 
  HomeIcon, 
  CalendarIcon, 
  AlertTriangleIcon, 
  UsersIcon, 
  BarChartIcon, 
  UserIcon, 
  LogOutIcon,
  ChevronDownIcon,
  MoonIcon,
  SunIcon,
  FileTextIcon,
  ActivityIcon,
  SettingsIcon,
  RotateCcwIcon,
} from 'lucide-react';
import { useAuth } from '@/stores/auth';
import { useRoleCheck } from '@/components/ProtectedRoute';
import { SeasonSelector } from '@/components/SeasonSelector';
import type { UserRole } from '@/types';
import CampionatoSelector from '@/components/CampionatoSelector';
import { applyTheme, getStoredTheme, storeTheme, type ThemeMode } from '@/lib/theme';
import { apiClient } from '@/services/apiClient';

interface SidebarItem {
  label: string;
  path: string;
  icon: React.ComponentType<any>;
  roles?: UserRole[];
  badge?: string | number;
}

interface SidebarGroup {
  label: string;
  items: SidebarItem[];
  roles?: UserRole[];
}

const sidebarConfig: SidebarGroup[] = [
  {
    label: 'Utente',
    items: [
      {
        label: 'Dashboard',
        path: '/',
        icon: HomeIcon,
      },
      {
        label: 'Le Mie Gare', // 🔥 UNIFICATO: Sostituisce Lista Gare, Le Mie Gare, Gare Assegnate
        path: '/utente/gare',
        icon: CalendarIcon,
      },
      {
        label: 'Le Mie Statistiche',
        path: '/utente/statistiche',
        icon: BarChartIcon,
      },
    ],
  },
  {
    label: 'Amministratore',
    roles: ['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente'],
    items: [
      {
        label: 'Inserisci CRA01',
        path: '/admin/inserisci-cra01',
        icon: AlertTriangleIcon,
        roles: ['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente'],
      },
      {
        label: 'Inserisci utenti',
        path: '/admin/inserisci-utenti',
        icon: UsersIcon,
        roles: ['ref_informatico'],
      },
      {
      label: 'Lista gare',
      path: '/admin/gare',
      icon: CalendarIcon, // è già importato in alto
      roles: ['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente'],
      },
      {
        label: 'Statistiche',
        path: '/admin/statistiche',
        icon: BarChartIcon,
        roles: ['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente'],
      },
      {
        label: 'Monitoraggio rapportini',
        path: '/admin/monitoraggio-rapportini',
        icon: FileTextIcon,
        roles: ['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente'],
      },
      {
        label: 'Riapertura rapportini',
        path: '/admin/rapportini-riaperture',
        icon: RotateCcwIcon,
        roles: ['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente'],
      },
      {
        label: 'Monitoraggio uso portale',
        path: '/admin/monitoraggio-uso-portale',
        icon: ActivityIcon,
        roles: ['organo_tecnico', 'ref_informatico', 'presidente', 'vice_presidente'],
      },
      {
        label: 'Utenti',
        path: '/admin/utenti',
        icon: UsersIcon,
        roles: ['ref_informatico'],
      },
      {
        label: 'Impostazioni',
        path: '/admin/impostazioni',
        icon: SettingsIcon,
        roles: ['ref_informatico'],
      },
    ],
  },
];

export const MainLayout: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>(() => getStoredTheme());
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const label = [...sidebarConfig.flatMap((group) => group.items)]
      .sort((a, b) => b.path.length - a.path.length)
      .find((item) => location.pathname.startsWith(item.path))?.label || location.pathname;

    apiClient.logPortalUsage({
      event_type: 'page_view',
      action_label: `Pagina: ${label}`,
      path: location.pathname,
      page_path: `${location.pathname}${location.search}`,
    });
  }, [location.pathname, location.search]);
  const { user, logout } = useAuth();
  const { hasAnyRole } = useRoleCheck();

  // Load sidebar collapsed state from localStorage
  useEffect(() => {
    const collapsed = localStorage.getItem('arma4you.sidebarCollapsed');
    if (collapsed !== null) {
      setSidebarCollapsed(JSON.parse(collapsed));
    }
    applyTheme(getStoredTheme());
  }, []);

  // Save sidebar state to localStorage
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('arma4you.sidebarCollapsed', JSON.stringify(newState));
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    storeTheme(next);
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    return `${user.nome.charAt(0)}${user.cognome.charAt(0)}`.toUpperCase();
  };

  const getPageTitle = () => {
    const path = location.pathname;
    
    // Find matching menu item
    for (const group of sidebarConfig) {
      for (const item of group.items) {
        if (item.path === path) {
          return item.label;
        }
      }
    }
    
    // Fallback titles for specific paths
    if (path === '/profilo') return 'Profilo';
    if (path.startsWith('/admin')) return 'Amministrazione';
    if (path.startsWith('/utente')) return 'Area Utente';
    
    return 'MARES';
  };

  const isItemActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    
    // 🔥 MAPPATURA ROUTE: Le vecchie route puntano alla nuova pagina unificata
    const routeMapping: Record<string, string> = {
      '/utente/gare': '/utente/gare',
      '/utente/lista-gare': '/utente/gare',
      '/utente/visualizza-gare': '/utente/gare', 
      '/utente/gare-assegnate': '/utente/gare',
    };
    
    const mappedPath = routeMapping[path] || path;
    const currentMappedPath = routeMapping[location.pathname] || location.pathname;
    
    return currentMappedPath.startsWith(mappedPath);
  };

  const canShowGroup = (group: SidebarGroup) => {
    if (!group.roles) return true;
    return hasAnyRole(group.roles);
  };

  const canShowItem = (item: SidebarItem) => {
    if (!item.roles) return true;
    return hasAnyRole(item.roles);
  };

  return (
    <div className="app-shell h-screen flex overflow-hidden bg-gray-50 text-gray-900">
      {/* Sidebar - Desktop */}
      <div className={`hidden md:flex md:flex-shrink-0 transition-all duration-300 ${
        sidebarCollapsed ? 'md:w-16' : 'md:w-64'
      }`}>
        <div className="flex flex-col w-full">
          <div className="flex items-center h-16 flex-shrink-0 px-3 bg-primary-800 shadow-sm sidebar-brand">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center brand-mark">
                <img
                  src="/Logo_aia_2021.png"
                  alt="AIA"
                  className="h-8 w-8 object-contain"
                />
              </div>
              {!sidebarCollapsed && (
                <span className="ml-3 text-white font-semibold text-lg">MARES</span>
              )}
            </div>
          </div>
          
          <div className="flex-1 flex flex-col overflow-y-auto bg-white border-r border-gray-200 sidebar-surface">
            <nav className="sidebar-nav flex-1 px-3 py-5 space-y-5">
              {sidebarConfig.map((group) => {
                if (!canShowGroup(group)) return null;
                
                return (
                  <div key={group.label} className="sidebar-group">
                    {!sidebarCollapsed && (
                      <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        {group.label}
                      </h3>
                    )}
                    
                    {group.items.map((item) => {
                      if (!canShowItem(item)) return null;
                      
                      const Icon = item.icon;
                      const active = isItemActive(item.path);
                      
                      return (
                        <button
                          key={item.path}
                          onClick={() => navigate(item.path)}
                          className={`sidebar-link w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                            active
                              ? 'bg-primary-100 text-primary-900 border-r-2 border-primary-500'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                          title={sidebarCollapsed ? item.label : ''}
                        >
                          <Icon className={`flex-shrink-0 h-5 w-5 ${
                            active ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                          }`} />
                          {!sidebarCollapsed && (
                            <span className="ml-3 truncate">{item.label}</span>
                          )}
                          {!sidebarCollapsed && item.badge && (
                            <span className="ml-auto bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">
                              {item.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </nav>
          </div>
      {/* Footer versione */}
      <div className="px-3 py-2 border-t border-gray-200 text-gray-500">
        {sidebarCollapsed ? (
          <div className="text-[10px] text-center">Alpha 1</div>
        ) : (
          <div className="text-xs">
            Versione <span className="font-medium text-gray-700">1.0</span>
          </div>
        )}
      </div>
    </div>
  </div>


      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setMobileMenuOpen(false)} />
          <div className="mobile-sidebar relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                <XIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            
            {/* Mobile sidebar content */}
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-4 mb-4">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center brand-mark">
                  <img
                    src="/Logo_aia_2021.png"
                    alt="AIA"
                    className="h-8 w-8 object-contain"
                  />
                </div>
                <span className="ml-3 text-gray-800 font-semibold text-lg">MARES</span>
              </div>
              
              <nav className="sidebar-nav px-3 space-y-5">
                {sidebarConfig.map((group) => {
                  if (!canShowGroup(group)) return null;
                  
                  return (
                    <div key={group.label} className="sidebar-group">
                      <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        {group.label}
                      </h3>
                      
                      {group.items.map((item) => {
                        if (!canShowItem(item)) return null;
                        
                        const Icon = item.icon;
                        const active = isItemActive(item.path);
                        
                        return (
                          <button
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={`sidebar-link w-full group flex items-center px-3 py-2.5 text-sm font-medium rounded-md ${
                              active
                                ? 'bg-primary-100 text-primary-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <Icon className={`mr-3 flex-shrink-0 h-5 w-5 ${
                              active ? 'text-primary-500' : 'text-gray-400'
                            }`} />
                            {item.label}
                            {item.badge && (
                              <span className="ml-auto bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">
                                {item.badge}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </nav>
            </div>
      <div className="px-4 py-3 border-t border-gray-200 text-xs text-gray-500">
        Versione <span className="font-medium text-gray-700">1.0</span>
      </div>
    </div>
  </div>
      )}

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Header */}
        <div className="relative z-10 flex-shrink-0 flex items-center h-16 bg-white shadow-sm border-b border-gray-200 app-header">

          <button
            onClick={toggleSidebar}
            className="hidden md:flex px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 hover:text-gray-700"
          >
            <MenuIcon className="h-6 w-6" />
          </button>
          
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
          >
            <MenuIcon className="h-6 w-6" />
          </button>

          <div className="header-inner flex-1 px-4 flex justify-between items-center min-w-0">
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-gray-900">{getPageTitle()}</h1>
            </div>

            <div className="header-actions ml-3 flex items-center md:ml-6 gap-2 md:gap-3">
              <button
                type="button"
                onClick={toggleTheme}
                className="theme-toggle inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                title={theme === 'dark' ? 'Tema chiaro' : 'Tema scuro'}
                aria-label={theme === 'dark' ? 'Attiva tema chiaro' : 'Attiva tema scuro'}
              >
                {theme === 'dark' ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
              </button>

              {/* Season Selector */}
              <SeasonSelector />
              <CampionatoSelector className="ml-3" />


              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {getUserInitials()}
                    </span>
                  </div>
                  <ChevronDownIcon className="ml-2 h-4 w-4 text-gray-400" />
                </button>

                {userMenuOpen && (
                  <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {user?.nome} {user?.cognome}
                      </p>
                      <p className="text-xs text-gray-500">
                        {user?.meccanografico}
                      </p>
                    </div>
                    
                    <button
                      onClick={() => navigate('/profilo')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <UserIcon className="mr-3 h-4 w-4" />
                      Profilo
                    </button>
                    
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LogOutIcon className="mr-3 h-4 w-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="app-main flex-1 relative overflow-y-auto focus:outline-none">
          <div className="page-frame py-5 md:py-7">
            <div className="page-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
